import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import SelectFetchLoading from 'components/SelectFetchLoading';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Account } from 'types/Account';
import { Camera } from 'types/Camera';
import useGetCustomMosaic from './useGetCustomMosaic';

const MOSAIC_ID_PARAM = 'mosaicId';

function CustomMosaicSelect({
  accountSelected,
  setCamNamesToShow,
  cameras,
}: {
  accountSelected: Account;
  setCamNamesToShow: React.Dispatch<React.SetStateAction<string[] | null>>;
  cameras: Camera[] | undefined;
}) {
  const { t } = useTranslation(['_common', 'cameras']);
  const [searchParams, setSearchParams] = useSearchParams();
  const { mosaics, loading, error } = useGetCustomMosaic({ accountSelected });

  const mosaicId = React.useMemo(() => searchParams.get(MOSAIC_ID_PARAM) || '', [searchParams]);

  const mosaicsFiltered = React.useMemo(() => {
    return mosaics?.filter((mosaic) =>
      cameras?.some((camera) => mosaic.cameras.includes(camera.name)),
    );
  }, [mosaics, cameras]);

  React.useEffect(
    function filterMosaic() {
      if (!mosaicId) {
        setCamNamesToShow(null);
      } else {
        setCamNamesToShow(
          mosaicsFiltered?.find((mosaic) => mosaic.id === Number(mosaicId))?.cameras || null,
        );
      }
    },
    [mosaicsFiltered, mosaicId, setCamNamesToShow],
  );

  if (loading) {
    return <SelectFetchLoading label={t('_common:mosaic')} />;
  }

  if (error) {
    throw error;
  }

  return (
    <FormControl sx={{ width: 300 }}>
      <InputLabel id="custom-mosaic-label">{t('_common:mosaic')}</InputLabel>
      <Select
        labelId="custom-mosaic-label"
        id="custom-mosaic"
        value={mosaicId || 'all'}
        onChange={(e) =>
          setSearchParams({
            mosaicId: e.target.value === 'all' ? '' : e.target.value,
          })
        }
        label={t('_common:mosaic')}
      >
        <MenuItem value={'all'}>{t('cameras:all_cameras')}</MenuItem>
        {mosaicsFiltered?.map((mosaic) => (
          <MenuItem key={mosaic.id} value={String(mosaic.id)}>
            {mosaic.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default CustomMosaicSelect;
